import jsPDF from 'jspdf';

export const DEFAULT_RECEIPT_CONFIG = Object.freeze({
  enabled: true,
  title: 'TELCOFAIR PAYMENT RECEIPT',
  prefix: 'TF-RCPT-',
  accentColor: '#2E6F8E',
  footer: 'Thank you for your payment.',
  terms: 'This receipt confirms a payment recorded and approved by TELCOFAIR.',
  logoUrl: '',
  showCompanyLogo: true,
  showPaymentMethod: true,
  showPackage: true,
  emailOnApproval: true,
});

const clean = (value, fallback = '—') => {
  const text = String(value ?? '').trim();
  return text || fallback;
};

export const money = (amount, currency = 'EUR') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'EUR' }).format(Number(amount || 0)); }
  catch (_) { return `${currency || 'EUR'} ${Number(amount || 0).toFixed(2)}`; }
};

export const resolveReceiptConfig = (runtime = {}) => ({
  ...DEFAULT_RECEIPT_CONFIG,
  ...(runtime?.payments?.receipt || {}),
});

export const receiptNumberFor = (payment = {}, config = DEFAULT_RECEIPT_CONFIG) => {
  if (payment.receiptNumber) return payment.receiptNumber;
  const suffix = String(payment.id || payment.paymentId || payment.transactionReference || Date.now())
    .replace(/[^a-zA-Z0-9]/g, '').slice(-12).toUpperCase();
  return `${config.prefix || 'TF-RCPT-'}${suffix || Date.now()}`;
};

export function createPaymentReceiptPdf(payment = {}, configInput = {}) {
  const config = { ...DEFAULT_RECEIPT_CONFIG, ...(configInput || {}) };
  const receiptNumber = receiptNumberFor(payment, config);
  const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const accent = /^#[0-9a-f]{6}$/i.test(config.accentColor || '') ? config.accentColor : DEFAULT_RECEIPT_CONFIG.accentColor;

  pdf.setFillColor(accent);
  pdf.rect(0, 0, pageW, 8, 'F');
  pdf.setTextColor(24, 34, 53);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text(clean(config.title, DEFAULT_RECEIPT_CONFIG.title), 42, 55);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(95, 113, 132);
  pdf.text(`Receipt ${receiptNumber}`, 42, 73);
  pdf.text(`Approved ${clean(payment.approvedAtText || payment.paymentDate || payment.approvedAt || new Date().toISOString())}`, 42, 87);

  pdf.setDrawColor(225, 232, 237);
  pdf.line(42, 102, pageW - 42, 102);

  const rows = [
    ['Company', clean(payment.companyName)],
    ['Customer', clean(payment.userName || payment.customerName)],
    ['Email', clean(payment.email)],
    ['Payment type', clean(String(payment.paymentType || 'payment').replace(/_/g, ' '))],
    ['Amount', money(payment.amount, payment.currency)],
    ...(config.showPaymentMethod !== false ? [['Payment method', clean(String(payment.paymentMethod || payment.provider || '').replace(/_/g, ' '))]] : []),
    ['Transaction reference', clean(payment.transactionReference || payment.providerReference || payment.sessionId)],
    ...(config.showPackage !== false ? [['Package / plan', clean(payment.packageName || payment.planName)]] : []),
    ['Status', clean(payment.status || 'approved')],
  ];

  let y = 128;
  rows.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(95, 113, 132);
    pdf.text(label, 42, y);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(24, 34, 53);
    const wrapped = pdf.splitTextToSize(String(value), pageW - 190);
    pdf.text(wrapped, 170, y);
    y += Math.max(22, wrapped.length * 12 + 5);
  });

  y += 10;
  pdf.setDrawColor(225, 232, 237);
  pdf.line(42, y, pageW - 42, y);
  y += 22;
  if (config.terms) {
    pdf.setFontSize(8);
    pdf.setTextColor(95, 113, 132);
    const terms = pdf.splitTextToSize(clean(config.terms, ''), pageW - 84);
    pdf.text(terms, 42, y);
    y += terms.length * 10 + 14;
  }
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(accent);
  pdf.setFontSize(9);
  pdf.text(clean(config.footer, DEFAULT_RECEIPT_CONFIG.footer), 42, Math.min(y, 785));

  return { pdf, receiptNumber };
}

export function downloadPaymentReceipt(payment = {}, config = {}) {
  const { pdf, receiptNumber } = createPaymentReceiptPdf(payment, config);
  pdf.save(`${receiptNumber}.pdf`);
  return receiptNumber;
}

export function paymentReceiptAttachment(payment = {}, config = {}) {
  const { pdf, receiptNumber } = createPaymentReceiptPdf(payment, config);
  const dataUri = pdf.output('datauristring');
  return {
    receiptNumber,
    attachment: (() => {
      const contentBase64 = String(dataUri || '').split(',')[1] || '';
      return {
        filename: `${receiptNumber}.pdf`,
        contentBase64,
        content: contentBase64,
        encoding: 'base64',
        contentType: 'application/pdf',
      };
    })(),
  };
}

export function paymentReceiptEmailHtml(payment = {}, configInput = {}) {
  const config = { ...DEFAULT_RECEIPT_CONFIG, ...(configInput || {}) };
  const receiptNumber = receiptNumberFor(payment, config);
  const row = (label, value) => `<tr><td style="padding:7px 10px;color:#6b7a8c;font-size:12px;border-bottom:1px solid #edf1f4;width:38%">${label}</td><td style="padding:7px 10px;color:#182235;font-size:12px;font-weight:600;border-bottom:1px solid #edf1f4">${clean(value)}</td></tr>`;
  return `<div style="font-family:Arial,sans-serif;max-width:640px;margin:auto;color:#182235"><div style="height:6px;background:${config.accentColor || DEFAULT_RECEIPT_CONFIG.accentColor};border-radius:8px 8px 0 0"></div><div style="padding:22px;border:1px solid #e8eef2;border-top:0;border-radius:0 0 8px 8px"><h2 style="margin:0 0 6px">${clean(config.title, DEFAULT_RECEIPT_CONFIG.title)}</h2><p style="margin:0 0 18px;color:#6b7a8c;font-size:13px">Receipt ${receiptNumber}</p><table style="width:100%;border-collapse:collapse">${row('Company', payment.companyName)}${row('Amount', money(payment.amount, payment.currency))}${config.showPaymentMethod !== false ? row('Payment method', String(payment.paymentMethod || payment.provider || '').replace(/_/g, ' ')) : ''}${row('Transaction reference', payment.transactionReference || payment.providerReference || payment.sessionId)}${config.showPackage !== false ? row('Package / plan', payment.packageName || payment.planName) : ''}${row('Status', payment.status || 'approved')}</table><p style="margin:18px 0 0;color:#6b7a8c;font-size:12px">${clean(config.footer, DEFAULT_RECEIPT_CONFIG.footer)}</p></div></div>`;
}
